/**
 * B.H. Copyright (c) 2026 Yemot HaMashiach Ltd.
 * All Rights Reserved.
 *
 * This software is the confidential and proprietary information of
 * Yemot HaMashiach Ltd. ("Confidential Information"). You shall not
 * disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into
 * with Yemot HaMashiach Ltd.
 *
 * Unauthorized copying of this file, via any medium, is strictly prohibited.
 */
import React, { useState, useEffect } from 'react';
import '../../styles.css';
import PageShell from '../PageShell';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import cookie from 'js-cookie';
import axios from 'axios';
import { API_URL, APP_NAME_SIMEHADRIN, getActiveAppName } from '../../Constants';
import { showErrorDialog } from '../../features/errorDialog';
import { logout } from '../../features/user';
import { handleApiErrorMessage } from '../../Helper';

const PERMISSIONS_META = [
  {
    key: 'MUST_SIGN_UP_FOR_CALL_FILTERING',
    label: 'חובה הרשמה לסינון שיחות',
    desc: 'מנויים חייבים להירשם כדי לעבור את מסנן השיחות',
    type: 'boolean',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/>
        <path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/>
      </svg>
    ),
  },
  {
    key: 'DEFAULT_CALL_FILTERING_WITHOUT_MOBILES',
    label: 'סינון ברירת מחדל ללא סלולרים',
    desc: 'מספרי סלולר לא יהיו כפופים לסינון שיחות כברירת מחדל',
    type: 'boolean',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="5" y="2" width="14" height="20" rx="2"/><line x1="12" y1="18" x2="12.01" y2="18"/>
      </svg>
    ),
  },
  {
    key: 'CALL_FILTERING_AUTO_REGISTRATION',
    label: 'רישום אוטומטי לסינון שיחות',
    desc: 'מנויים ירשמו אוטומטית לסינון שיחות בעת הצטרפות',
    type: 'boolean',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="16 3 21 3 21 8"/><line x1="4" y1="20" x2="21" y2="3"/>
        <polyline points="21 16 21 21 16 21"/><line x1="15" y1="15" x2="21" y2="21"/>
      </svg>
    ),
  },
  {
    key: 'MAX_OFF_VALID_CALLS',
    label: 'מקסימום שיחות תקפות מחוץ לרשימה',
    desc: 'מספר מקסימלי של שיחות תקפות ממספרים שאינם ברשימה (null = ללא הגבלה)',
    type: 'number',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/>
        <line x1="6" y1="20" x2="6" y2="14"/>
      </svg>
    ),
  },
  {
    key: 'BLCOK_HIDE_CALLERD_ID_IN_DIAL_IN',
    label: 'חסימת הסתרת מזהה מתקשר בשיחות נכנסות',
    desc: 'מנע ממתקשרים להסתיר את המספר שלהם בשיחות נכנסות',
    type: 'boolean',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94"/>
        <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/>
        <line x1="1" y1="1" x2="23" y2="23"/>
      </svg>
    ),
  },
  {
    key: 'INCOMING_SMS_BLOCK',
    label: 'חסימת SMS נכנסים',
    desc: 'חסום קבלת הודעות SMS נכנסות לקהילה',
    type: 'boolean',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
        <line x1="1" y1="1" x2="23" y2="23"/>
      </svg>
    ),
  },
  {
    key: 'SMS_TO_CALLING_FOR_FREE_FOR_NOT_HLR_ROUTE',
    label: 'העברת SMS נכנס בשיחה ללא עלות',
    desc: 'הודעת SMS שלא מגיעה למנוי בדרך אחרת תועבר אליו בשיחה קולית',
    type: 'boolean',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/><path d="M8 9h8M8 13h5"/>
      </svg>
    ),
  },
  {
    key: 'OFF_VALID_IN_CALLS_IF_NOT_IL_NUMBER',
    label: 'ביטול אימות מתקשרים מיעדים לא ישראליים',
    desc: 'שיחות ממספרים לא ישראליים לא יידרשו לעבור אימות',
    type: 'boolean',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/>
        <path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/>
      </svg>
    ),
  },
  {
    key: 'DIAL_111',
    label: 'חיוג לכוכבית 111',
    desc: 'מנויי הקהילה רשאים לחייג לכוכבית 111',
    type: 'boolean',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.13.96.36 1.9.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0122 16.92z"/>
      </svg>
    ),
  },
  {
    key: 'BLOCK_USE_CUSTOMER_BLOCKS_REPORTS',
    label: 'חסימת שימוש במערכת דיווח המספרים',
    desc: 'המנויים לא יוכלו לחייג למערכת הדיווח ב*112',
    type: 'boolean',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 11H3v10h6V11zM21 3h-6v18h6V3zM15 7H9v14h6V7z"/><line x1="2" y1="2" x2="22" y2="22"/>
      </svg>
    ),
  },
];

// בסים מהדרין מדיניות ה-SMS נאכפת במרוכז, ולכן הקהילה לא רואה את ההגדרות האלה.
const SIMEHADRIN_CENTRAL_KEYS = ['INCOMING_SMS_BLOCK', 'SMS_TO_CALLING_FOR_FREE_FOR_NOT_HLR_ROUTE'];
const VISIBLE_PERMISSIONS = getActiveAppName() === APP_NAME_SIMEHADRIN
  ? PERMISSIONS_META.filter(({ key }) => !SIMEHADRIN_CENTRAL_KEYS.includes(key))
  : PERMISSIONS_META;

/* 3-state values for a boolean permission:
   'yes' → send true | 'no' → send false | 'ignore' → OMIT the key from the payload
   (the server treats a missing key as "inherit next in the chain"). */
const PERM_YES = 'yes';
const PERM_NO = 'no';
const PERM_IGNORE = 'ignore';

/* Map a loaded permission value to its 3-state control value.
   true → yes | false → no | null/undefined (absent) → ignore. */
function toTriState(value) {
  if (value === true) return PERM_YES;
  if (value === false) return PERM_NO;
  return PERM_IGNORE;
}

export default function CommunitySettings() {
  const [perms, setPerms]       = useState(null);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [saved, setSaved]       = useState(false);
  const [error, setError]       = useState(null);

  const { t }    = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const token   = cookie.get('token');
  const headers = { token };

  useEffect(() => {
    axios.get(API_URL + '/permissions', { headers })
      .then(({ data }) => {
        if (data.responseStatus === 'OK') {
          setPerms({ ...data.permissions });
        } else {
          handleApiErrorMessage(data.message, t, dispatch, showErrorDialog, logout, navigate);
        }
      })
      .catch(() => setError('שגיאה בטעינת ההגדרות'))
      .finally(() => setLoading(false));
  }, []);

  // Set a boolean permission to one of the 3 states.
  // yes → true, no → false, ignore → null (kept in state, OMITTED on save).
  const handleTriState = (key, state) => {
    const value = state === PERM_YES ? true : state === PERM_NO ? false : null;
    setPerms(prev => ({ ...prev, [key]: value }));
    setSaved(false);
  };

  const handleNumber = (key, val) => {
    const parsed = val === '' ? null : parseInt(val, 10);
    setPerms(prev => ({ ...prev, [key]: isNaN(parsed) ? null : parsed }));
    setSaved(false);
  };

  // Build the payload sent to PUT /permissions:
  //  - boolean permissions in "ignore" (null) are OMITTED so the server inherits
  //    the next value in the chain; yes/no are sent as true/false.
  //  - numeric permissions are sent as-is (null = no limit).
  const buildPayload = () => {
    const payload = {};
    PERMISSIONS_META.forEach(({ key, type }) => {
      const value = perms[key];
      if (type === 'boolean') {
        if (value === true || value === false) payload[key] = value; // omit on ignore (null/undefined)
      } else {
        payload[key] = value ?? null;
      }
    });
    return payload;
  };

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      const { data } = await axios.put(API_URL + '/permissions', buildPayload(), { headers });
      if (data.responseStatus === 'OK') {
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      } else {
        handleApiErrorMessage(data.message, t, dispatch, showErrorDialog, logout, navigate);
      }
    } catch {
      setError('שגיאה בשמירת ההגדרות');
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageShell title="הגדרות קהילה" subtitle="ניהול הרשאות ופרמטרי מערכת">
    <div className='cs-page'>

      {/* ── Header ── */}
      <div className='cs-header'>
        <div className='cs-header-right'>
          <div className='cs-header-icon'>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="3"/>
              <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/>
            </svg>
          </div>
          <div>
            <div className='cs-header-title'>הגדרות קהילה</div>
            <div className='cs-header-sub'>ניהול הרשאות ופרמטרי מערכת</div>
          </div>
        </div>
        <button className='cs-save-btn' onClick={handleSave} disabled={saving || loading}>
          {saving ? (
            <span className='cs-save-spinner' />
          ) : (
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"/>
              <polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/>
            </svg>
          )}
          {saving ? 'שומר...' : 'שמור הגדרות'}
        </button>
      </div>

      {/* ── Success banner ── */}
      {saved && (
        <div className='cs-success'>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
          ההגדרות נשמרו בהצלחה
        </div>
      )}

      {/* ── Error banner ── */}
      {error && (
        <div className='cs-error'>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          {error}
          <button className='cs-error-close' onClick={() => setError(null)}>✕</button>
        </div>
      )}

      {/* ── Permissions list ── */}
      <div className='cs-card'>
        <div className='cs-card-head'>
          <h3 className='cs-card-title'>הרשאות מערכת</h3>
          <span className='cs-card-badge'>
            {loading ? '...' : `${VISIBLE_PERMISSIONS.length} הגדרות`}
          </span>
        </div>

        {!loading && perms && (
          <div className='cs-tristate-hint'>
            לכל הרשאה ניתן לבחור <strong>כן</strong> / <strong>לא</strong>, או <strong>לא מוגדר</strong> —
            במצב "לא מוגדר" הקהילה אינה קובעת דבר, וההחלטה עוברת לרמה הבאה בשרשרת ובסוף לברירת המחדל.
          </div>
        )}

        {loading ? (
          <div className='cs-loading'>
            <div className='cs-loading-spinner' />
            <span>טוען הגדרות...</span>
          </div>
        ) : perms ? (
          <div className='cs-perms-list'>
            {VISIBLE_PERMISSIONS.map(({ key, label, desc, type, icon }) => (
              <div key={key} className='cs-perm-row'>
                <div className='cs-perm-icon'>{icon}</div>
                <div className='cs-perm-info'>
                  <span className='cs-perm-label'>{label}</span>
                  <span className='cs-perm-desc'>{desc}</span>
                </div>
                <div className='cs-perm-control'>
                  {type === 'boolean' ? (
                    <div className='cs-tristate' role='radiogroup' aria-label={label}>
                      {[
                        { s: PERM_YES,    txt: 'כן' },
                        { s: PERM_IGNORE, txt: 'לא מוגדר' },
                        { s: PERM_NO,     txt: 'לא' },
                      ].map(({ s, txt }) => {
                        const active = toTriState(perms[key]) === s;
                        return (
                          <button
                            key={s}
                            type='button'
                            role='radio'
                            aria-checked={active}
                            className={`cs-tristate-opt cs-tristate-opt--${s}${active ? ' is-active' : ''}`}
                            onClick={() => handleTriState(key, s)}
                          >
                            {txt}
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div className='cs-num-wrap'>
                      <input
                        type='number'
                        className='cs-num-input'
                        value={perms[key] ?? ''}
                        placeholder='ללא הגבלה'
                        min={0}
                        onChange={e => handleNumber(key, e.target.value)}
                      />
                      {perms[key] !== null && perms[key] !== undefined && (
                        <button className='cs-num-clear' onClick={() => handleNumber(key, '')}>✕</button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </div>

    </div>
    </PageShell>
  );
}
